'use client';

import { useQuery } from '@tanstack/react-query';
import {
  type LeadEventWithStatusDto,
  LeadEventLifecycleStatus,
  LeadEventRosterMode,
  Permission,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  Skeleton,
} from '@up2stay/up2promo-ui';
import {
  Calendar,
  CalendarDays,
  CalendarPlus,
  ChevronRight,
  MapPin,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { CreateLeadEventDialog } from '@/core/components/leads/create-lead-event-dialog';
import {
  ListEmptyState,
  ListPageBody,
  resourceInteractiveCardClass,
} from '@/core/components/layout';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { fetchAllLeadEventsPages } from '@/core/lib/lead-events-fetch-all';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { leadEventsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

type LeadsEventsHubProps = {
  workspaceId: string;
  /** PageHeader already shows title/description/create CTA. */
  hideIntro?: boolean;
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
};

const DESKTOP_GRID =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_150px_90px_110px_24px] gap-3.5';

function formatRange(
  startIso: string,
  endIso: string,
  locale: string,
  template: string,
): string {
  const fmt = new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return template
    .replace('{start}', fmt.format(new Date(startIso)))
    .replace('{end}', fmt.format(new Date(endIso)));
}

function statusBadgeVariant(
  status: LeadEventWithStatusDto['status'],
): 'default' | 'info' | 'secondary' {
  if (status === LeadEventLifecycleStatus.ACTIVE) return 'default';
  if (status === LeadEventLifecycleStatus.UPCOMING) return 'info';
  return 'secondary';
}

function teamLabel(
  ev: LeadEventWithStatusDto,
  everyoneLabel: string,
  peopleTemplate: string,
): string {
  if (ev.rosterMode === LeadEventRosterMode.EVERYONE) return everyoneLabel;
  const count = ev.rosterUserIds.length + ev.rosterTeamIds.length;
  return peopleTemplate.replace('{count}', String(Math.max(count, 1)));
}

function unusualMonths(ev: LeadEventWithStatusDto): number {
  const start = new Date(ev.startsAt).getTime();
  const end = new Date(ev.endsAt).getTime();
  const months = (end - start) / (1000 * 60 * 60 * 24 * 30.44);
  return months > 14 ? Math.round(months) : 0;
}

export function LeadsEventsHub({
  workspaceId,
  hideIntro = false,
  createOpen: createOpenControlled,
  onCreateOpenChange,
}: LeadsEventsHubProps) {
  const fullDict = useDictionary();
  const dict = fullDict.leads.events;
  const locale = useLocale();
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const eventsEnabled = isFeatureEnabled('feature.lead_events');
  const canManage = can(Permission.EVENTS_MANAGE);
  const [createOpenUncontrolled, setCreateOpenUncontrolled] = useState(false);
  const createOpen = createOpenControlled ?? createOpenUncontrolled;
  const setCreateOpen = onCreateOpenChange ?? setCreateOpenUncontrolled;

  const {
    data: events = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: leadEventsQueryKeys.all(workspaceId),
    queryFn: async () => {
      return fetchAllLeadEventsPages(workspaceId);
    },
    enabled: !!workspaceId && eventsEnabled && !entitlementsLoading,
    staleTime: 30_000,
    retry: false,
  });

  const grouped = useMemo(() => {
    const active: LeadEventWithStatusDto[] = [];
    const upcoming: LeadEventWithStatusDto[] = [];
    const past: LeadEventWithStatusDto[] = [];
    for (const ev of events) {
      if (ev.status === LeadEventLifecycleStatus.ACTIVE) active.push(ev);
      else if (ev.status === LeadEventLifecycleStatus.UPCOMING)
        upcoming.push(ev);
      else past.push(ev);
    }
    return { active, upcoming, past };
  }, [events]);

  const openEvent = (id: string) => {
    navigateShell(shellPaths.leadEventDetail(id));
  };

  if (entitlementsLoading) {
    return (
      <ListPageBody>
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </ListPageBody>
    );
  }

  if (!eventsEnabled) {
    return (
      <ListPageBody>
        <UpgradeCard feature="feature.lead_events" compact />
      </ListPageBody>
    );
  }

  const tableLabels = dict.table;
  const everyoneLabel = dict.form.rosterEveryone;
  const peopleTemplate = dict.table.peopleCount;

  const renderMobileRow = (ev: LeadEventWithStatusDto) => {
    const range = formatRange(ev.startsAt, ev.endsAt, locale, dict.card.dates);
    const location = ev.location?.trim() || dict.card.locationUnknown;
    const cover = ev.coverImage?.path
      ? getAbsoluteFileUrl(ev.coverImage.path)
      : null;
    const statusLabel =
      ev.status === LeadEventLifecycleStatus.ACTIVE
        ? dict.status.active
        : ev.status === LeadEventLifecycleStatus.UPCOMING
          ? dict.status.upcoming
          : dict.status.past;

    return (
      <button
        key={ev.id}
        type="button"
        className="w-full cursor-pointer text-left"
        onClick={() => openEvent(ev.id)}
      >
        <Card
          className={cn(
            'overflow-hidden',
            resourceInteractiveCardClass,
            ev.status === LeadEventLifecycleStatus.ACTIVE &&
              'border-[color:var(--ghost-accent-border)]',
          )}
        >
          {cover ? (
            <div className="h-24 w-full overflow-hidden bg-muted">
              <img src={cover} alt="" className="h-full w-full object-cover" />
            </div>
          ) : null}
          <CardContent className="flex flex-col gap-2 p-5">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 text-[15.5px] font-bold leading-tight text-foreground [overflow-wrap:anywhere]">
                {ev.name}
              </p>
              <Badge
                variant={statusBadgeVariant(ev.status)}
                className="shrink-0"
              >
                {statusLabel}
              </Badge>
            </div>
            <p className="flex items-center gap-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {location}
            </p>
            <p className="flex items-center gap-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {range}
            </p>
            {/* Same figures as the desktop row (DEV-820). */}
            <div className="mt-1 flex gap-6 border-t border-[color:var(--surface-border)] pt-3">
              <div>
                <div className="font-mono text-[15px] font-bold">
                  {ev.leadCount ?? 0}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {tableLabels.leads}
                </div>
              </div>
              <div>
                <div className="font-mono text-[15px] font-bold">
                  {ev.qualifiedPlusCount ?? 0}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {tableLabels.qualifiedPlus}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </button>
    );
  };

  const renderDesktopRow = (ev: LeadEventWithStatusDto, bordered: boolean) => {
    const range = formatRange(ev.startsAt, ev.endsAt, locale, dict.card.dates);
    const location = ev.location?.trim() || dict.card.locationUnknown;
    const isActive = ev.status === LeadEventLifecycleStatus.ACTIVE;
    const statusLabel =
      ev.status === LeadEventLifecycleStatus.ACTIVE
        ? dict.status.active
        : ev.status === LeadEventLifecycleStatus.UPCOMING
          ? dict.status.upcoming
          : dict.status.past;
    const team = teamLabel(ev, everyoneLabel, peopleTemplate);

    return (
      <button
        key={ev.id}
        type="button"
        onClick={() => openEvent(ev.id)}
        className={cn(
          DESKTOP_GRID,
          'w-full cursor-pointer items-center px-[18px] py-3.5 text-left hover:bg-muted/40',
          bordered && 'border-t border-[color:var(--surface-border)]',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-[10px]',
              isActive
                ? 'bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <Calendar className="size-[17px]" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate text-[14px] font-semibold">
                {ev.name}
              </span>
              <span
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  isActive
                    ? 'bg-primary/10 text-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    isActive ? 'bg-primary' : 'bg-muted-foreground',
                  )}
                  aria-hidden
                />
                {statusLabel}
              </span>
              {unusualMonths(ev) > 0 ? (
                <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-200">
                  {dict.detail.unusualDuration.replace(
                    '{count}',
                    String(unusualMonths(ev)),
                  )}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">
              {range}
            </div>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <MapPin className="size-[13px] shrink-0" aria-hidden />
          <span className="truncate">{location}</span>
        </div>
        <div className="flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-muted-foreground">
          <Users className="size-[13px] shrink-0" aria-hidden />
          {team}
        </div>
        <div>
          <div className="font-mono text-[15px] font-bold">
            {ev.leadCount ?? 0}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {tableLabels.leads}
          </div>
        </div>
        <div>
          <div className="font-mono text-[15px] font-bold">
            {ev.qualifiedPlusCount ?? 0}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {tableLabels.qualifiedPlus}
          </div>
        </div>
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
      </button>
    );
  };

  const renderDesktopSection = (
    title: string,
    items: LeadEventWithStatusDto[],
    showTopBorder: boolean,
  ) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div
          className={cn(
            'px-[18px] pb-0 pt-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
            showTopBorder &&
              'border-t border-[color:var(--surface-border)] pt-3.5',
          )}
        >
          {title}
        </div>
        {items.map((ev, index) => renderDesktopRow(ev, index > 0))}
      </div>
    );
  };

  return (
    <ListPageBody className="gap-4 md:gap-5">
      {hideIntro ? null : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-xl text-[12.5px] leading-relaxed text-muted-foreground">
            {dict.description}
          </p>
          {canManage ? (
            <Button
              type="button"
              className="min-h-11 w-full shrink-0 sm:w-auto"
              onClick={() => setCreateOpen(true)}
            >
              <CalendarPlus className="mr-2 h-4 w-4" />
              {dict.create}
            </Button>
          ) : null}
        </div>
      )}

      {error ? (
        <ListEmptyState
          tone="error"
          title={dict.loadError}
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              {fullDict.leads.loadError.retry}
            </Button>
          }
        />
      ) : isLoading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              className={cn('w-full', isMobile ? 'h-28' : 'h-16')}
            />
          ))}
        </div>
      ) : events.length === 0 ? (
        <ListEmptyState
          icon={<CalendarPlus className="h-6 w-6" aria-hidden />}
          title={dict.empty.title}
          description={dict.empty.description}
          action={
            canManage ? (
              <Button
                type="button"
                className="min-h-11"
                onClick={() => setCreateOpen(true)}
              >
                <CalendarPlus className="mr-2 h-4 w-4" />
                {dict.create}
              </Button>
            ) : null
          }
        />
      ) : isMobile ? (
        <div className="flex flex-col gap-4">
          {grouped.active.length > 0 ? (
            <section className="flex flex-col gap-3">
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {dict.status.active}
              </h3>
              {grouped.active.map(renderMobileRow)}
            </section>
          ) : null}
          {grouped.upcoming.length > 0 ? (
            <section className="flex flex-col gap-3">
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {dict.status.upcoming}
              </h3>
              {grouped.upcoming.map(renderMobileRow)}
            </section>
          ) : null}
          {grouped.past.length > 0 ? (
            <section className="flex flex-col gap-3">
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {dict.status.past}
              </h3>
              {grouped.past.map(renderMobileRow)}
            </section>
          ) : null}
        </div>
      ) : (
        <Card className="overflow-hidden border-[color:var(--surface-border)] shadow-[var(--shadow-card)]">
          <div
            className={cn(
              DESKTOP_GRID,
              'border-b border-[color:var(--surface-border)] px-[18px] py-2.5 text-[11.5px] text-muted-foreground',
            )}
          >
            <div>{tableLabels.event}</div>
            <div>{tableLabels.location}</div>
            <div>{tableLabels.team}</div>
            <div>{tableLabels.leads}</div>
            <div>{tableLabels.result}</div>
            <div />
          </div>
          {renderDesktopSection(dict.status.active, grouped.active, false)}
          {renderDesktopSection(
            dict.status.upcoming,
            grouped.upcoming,
            grouped.active.length > 0,
          )}
          {renderDesktopSection(
            dict.status.past,
            grouped.past,
            grouped.active.length > 0 || grouped.upcoming.length > 0,
          )}
        </Card>
      )}

      <CreateLeadEventDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaceId={workspaceId}
        onCreated={(id) => {
          void refetch();
          navigateShell(shellPaths.leadEventDetail(id));
        }}
      />
    </ListPageBody>
  );
}
